/**
 * Canonical investorProfile schema (v7) — single source of truth for shape + validation.
 *
 * This file is THE contract. Frontend reads/writes via this schema, backend Pydantic
 * mirrors it (api/models/investor_profile_schema.py), Postgres CHECK constraints
 * enforce a subset (supabase/migrations/*v7_constraints.sql), and a CI parity test
 * proves the three layers stay in sync.
 *
 * v7 = v6 + age + stateOfResidence (additive, no breaking changes to v6 fields).
 */

import { z } from 'zod';

export const SCHEMA_VERSION = 7;

// ---------------------------------------------------------------------------
// Enums (kept here so backend Pydantic mirror can verify same set)
// ---------------------------------------------------------------------------

export const ENTITY_TYPES = ['Individual', 'Joint', 'SMSF', 'Trust', 'Corporate'];
export const RISK_PROFILES = ['Conservative', 'Balanced', 'Growth', 'Aggressive'];
export const INVESTMENT_HORIZONS = ['2-5 years', '5-10 years', '10+ years'];
export const HOUSEHOLD_STATUSES = ['single', 'couple'];
export const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

// ---------------------------------------------------------------------------
// Sub-schemas
// ---------------------------------------------------------------------------

export const investmentEntitySchema = z.object({
  type: z.enum(ENTITY_TYPES).nullable(),
  name: z.string().nullable().optional(),
  abn: z.string().nullable().optional(),
  trusteeType: z.enum(['Individual', 'Corporate']).nullable().optional(),
});

export const applicantIncomeSchema = z.object({
  payg: z.number().min(0).default(0),
  selfEmployed: z.number().min(0).default(0),
  overtimeBonusCommission: z.number().min(0).default(0),
  investment: z.number().min(0).default(0),
  other: z.number().min(0).default(0),
});

export const householdIncomeSchema = z.object({
  primaryApplicant: applicantIncomeSchema,
  secondaryApplicant: applicantIncomeSchema.nullable(),
  status: z.enum(HOUSEHOLD_STATUSES).default('single'),
  dependants: z.number().int().min(0).max(10).default(0),
});

export const householdExpensesSchema = z.object({
  useHEM: z.boolean().default(true),
  livingExpensesMonthly: z.number().min(0).default(0),
  creditCardLimits: z.number().min(0).default(0),
  existingHomeLoanMonthly: z.number().min(0).default(0),
  otherLoanMonthly: z.number().min(0).default(0),
});

export const taxPositionSchema = z.object({
  marginalTaxRate: z.number().min(0).max(0.5).default(0.37),
  cgtDiscountEligible: z.boolean().default(true),
});

export const assetsSchema = z.object({
  cash: z.number().min(0).default(0),
});

export const geographicPreferencesSchema = z.object({
  states: z.array(z.enum(AU_STATES)).default([]),
  capitalCities: z.boolean().default(true),
  regionalAreas: z.boolean().default(false),
  preferredSuburbs: z.array(z.string()).default([]),
  excludedAreas: z.array(z.string()).default([]),
});

// ---------------------------------------------------------------------------
// Top-level v7 InvestorProfile schema
// ---------------------------------------------------------------------------

export const investorProfileV7Schema = z.object({
  // Schema version — the backend writes 7 and the investor_profiles_schema_version_present
  // Postgres CHECK requires >= 7. Optional: a profile that never reached the backend has none.
  schemaVersion: z.number().int().min(7).optional(),

  // Identity
  name: z.string().nullable(),
  email: z.string().nullable(),

  // Entity
  investmentEntity: investmentEntitySchema,

  // Risk + horizon + calibration (v7 additions: age + stateOfResidence)
  riskProfile: z.enum(RISK_PROFILES).nullable(),
  investmentHorizon: z.enum(INVESTMENT_HORIZONS).nullable(),
  age: z.number().int().min(18).max(100).nullable(),
  stateOfResidence: z.enum(AU_STATES).nullable(),

  // Financials
  householdIncome: householdIncomeSchema,
  householdExpenses: householdExpensesSchema,
  taxPosition: taxPositionSchema,
  assets: assetsSchema,

  // Preferences
  geographicPreferences: geographicPreferencesSchema,
}).passthrough(); // tolerate extra fields from backend extensions (preferredAssetClasses, etc.)

/**
 * Validate-and-return. Throws ZodError on invalid input.
 * Used by setInvestorProfile in PortfolioDataContext (Phase 5b).
 */
export function parseInvestorProfile(raw) {
  return investorProfileV7Schema.parse(raw);
}

/**
 * Safe-parse — returns { success, data, error } instead of throwing.
 * Used by warning-mode validation (Phase 5b → 5f flip).
 */
export function safeParseInvestorProfile(raw) {
  return investorProfileV7Schema.safeParse(raw);
}
